import React, { useEffect, useState } from 'react'
import { graphql } from 'gatsby'

import Layout from '../components/Layout/Layout'
import ShareIcon from '../images/icons/share.svg'

import pxToRem from '../utils/pxToRem'
import { HeadingSizeEnum } from '../components/Heading/Heading'
import { deviceMax } from '../theme/breakpoints'
import theme from '../theme/theme-style'
import Hero from '../components/Hero/Hero'
import CKEditorContent from '../components/CKEditorContent/CKEditorContent'
import { formatDateStory } from '../utils/FormatDate'
import ShareOverlay from '../components/ShareOverlay/ShareOverlay'
import ContentType from '../enums/ContentType'
import Seo from '../components/Seo/Seo'
import { TYPES_ENUM } from '../config/BreadCrumbsConfig'
import ChipVariantEnum from '../enums/ChipVariant'
import NotFoundPage from '../pages/404'
import useMediaQuery from '../hooks/useMediaQuery'
import {
  MOBILE_TITLE,
  StyledChip,
  StyledDate,
  StyledDecoration,
  StyledHeading,
  StyledIconsContainer,
  StyleDocumentCarousel,
  StyledSeparator,
  StyledShareIcon,
  StyledSlider,
  StyledStoryCategories,
  StyledStoryContainer,
  StyledStoryContent,
  StyledStoryDate,
  StyledStoryDateCategories,
  StyledStoryInfos,
  StyledVideo,
} from './story.styled'
import { CarouselVariantEnum } from '../components/Carousel/Carousel'
import NormalizeMedia from '../utils/NormalizeMedia'
import SlideDocument from '../components/Carousel/SlideDocument/SlideDocument'
import RichText from '../components/RichText/RichText'
import RedirectionHighlight from '../components/RedirectionHighlight/RedirectionHighlight'
import CarrouselBlock from '../components/CarrouselBlock/CarrouselBlock'
import BlockMixMediaIframe from '../components/BlockMixMediaIframe/BlockMixMediaIframe'

const StorySecondaryBlocksEnum = {
  componentRedirectionHighlight: 'componentRedirectionHighlight',
  componentSlider: 'componentSlider',
  contentBlocWithDocuments: 'contentBlocWithDocuments',
  contentMedia: 'contentMedia',
  componentRichText: 'componentRichText',
  contentIframe: 'contentIframe'
}

const getRightBlock = (block) => {
  const contentType = block.sys.contentType.sys.id
  const {
    blocDocuments,
    content,
    contentTextHtml,
    ctaLabel,
    ctaUrl,
    description,
    id,
    name,
    slides,
    title,
  } = block

  switch (contentType) {
    case StorySecondaryBlocksEnum.componentRedirectionHighlight:
      return (
        <RedirectionHighlight
          ctaLabel={ctaLabel}
          ctaUrl={ctaUrl}
          description={description}
          title={name}
        />
      )

    case StorySecondaryBlocksEnum.componentSlider:
      return <StyledSlider key={id} title={name} slides={slides} />

    case StorySecondaryBlocksEnum.contentBlocWithDocuments:
      const documents = SlideDocument(
        blocDocuments,
        CarouselVariantEnum.horizontal
      )
      return (
        <StyleDocumentCarousel
          key={id}
          variant={CarouselVariantEnum.document}
          orientation={CarouselVariantEnum.horizontal}
          title={name}
        >
          {documents}
        </StyleDocumentCarousel>
      )

    case StorySecondaryBlocksEnum.contentMedia:
      return <StyledVideo key={id}>{NormalizeMedia(block)}</StyledVideo>

    case StorySecondaryBlocksEnum.componentRichText:
      return (
        <StyledStoryContent key={id}>
          {title?.title && (
            <StyledHeading label={title?.title} size={HeadingSizeEnum.h3} />
          )}
          {contentTextHtml ? (
            <CKEditorContent html={contentTextHtml.content} />
          ) : (
            <RichText bodyRichText={content} />
          )}
        </StyledStoryContent>
      )

    case StorySecondaryBlocksEnum.contentIframe:
      return <BlockMixMediaIframe media={block} />

    default:
      return null
  }
}

const Story = ({ data, location }) => {
  if (data.contentfulTemplateStory === null) {
    return <NotFoundPage location={location} />
  }

  const {
    storyHtmlContent,
    backgroundColor,
    carrousel,
    carrouselPosition,
    date,
    categories,
    imageHeaderDesktop,
    imageHeaderMobile,
    slug,
    title,
    seoMetaDescription,
    secondaryBlocks,
  } = data.contentfulTemplatePageStory

  const [isSharing, setIsSharing] = useState(false)
  const [dateFormated, setFormatedDate] = useState(null)
  const isMobile = useMediaQuery(deviceMax.mobileL)

  useEffect(() => {
    setFormatedDate(formatDateStory(date))
  }, [])

  const ToggleShareOverlay = () => setIsSharing(!isSharing)

  const htmlContent = storyHtmlContent ? storyHtmlContent.content : ''

  const categoriesChips = categories?.map((element) => {
    return (
      <StyledChip
        key={element.id}
        label={element.title}
        variant={ChipVariantEnum.primary}
      />
    )
  })

  const blocks = secondaryBlocks?.map((block) => getRightBlock(block))

  const isCarrouselInBottom = carrouselPosition === 'Bottom'

  return (
    <Layout>
      <Seo
        title={title}
        description={seoMetaDescription}
        canonicalHref={location.pathname}
      />
      <Hero
        backgroundColor={backgroundColor}
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        pathname={location.pathname}
        slug={slug}
        title={isMobile ? MOBILE_TITLE : title}
        type={TYPES_ENUM.story}
      />
      {isSharing && (
        <ShareOverlay
          title={isMobile ? MOBILE_TITLE : title}
          setIsSharing={setIsSharing}
          contentType={ContentType.templatePageStory}
        />
      )}
      <StyledStoryContainer>
        {carrousel && !isCarrouselInBottom && (
          <CarrouselBlock items={carrousel.items} title={carrousel.title} />
        )}
        <StyledStoryContent>
          {isMobile ? (
            <StyledStoryInfos>
              <StyledStoryDateCategories>
                <StyledStoryDate>
                  <StyledDecoration $color="lime" />
                  <StyledDate>{dateFormated}</StyledDate>
                </StyledStoryDate>
                <StyledIconsContainer>
                  <StyledShareIcon
                    color={theme.colors.unique.transparent}
                    width={pxToRem(24)}
                    height={pxToRem(24)}
                    onClick={ToggleShareOverlay}
                  >
                    <ShareIcon />
                  </StyledShareIcon>
                </StyledIconsContainer>
              </StyledStoryDateCategories>
              <StyledStoryCategories>
                {categories && <StyledDecoration $color="blue" />}
                {categoriesChips}
              </StyledStoryCategories>
            </StyledStoryInfos>
          ) : (
            <StyledStoryInfos>
              <StyledStoryDateCategories>
                <StyledStoryDate>
                  <StyledDecoration $color="lime" />
                  <StyledDate>{dateFormated}</StyledDate>
                </StyledStoryDate>
                <StyledStoryCategories>
                  {categories && <StyledDecoration $color="blue" />}
                  {categoriesChips}
                </StyledStoryCategories>
              </StyledStoryDateCategories>

              <StyledIconsContainer>
                <StyledShareIcon
                  color={theme.colors.unique.transparent}
                  width={pxToRem(24)}
                  height={pxToRem(24)}
                  onClick={ToggleShareOverlay}
                >
                  <ShareIcon />
                </StyledShareIcon>
              </StyledIconsContainer>
            </StyledStoryInfos>
          )}
          <StyledSeparator />
          {isMobile && (
            <StyledHeading label={title} size={HeadingSizeEnum.h3} />
          )}
          <CKEditorContent html={htmlContent} />
        </StyledStoryContent>
        {blocks}
        {carrousel && isCarrouselInBottom && (
          <CarrouselBlock items={carrousel.items} title={carrousel.title} />
        )}
      </StyledStoryContainer>
    </Layout>
  )
}

export default Story

export const query = graphql`
  query StoryQuery($contentful_id: String!) {
    contentfulTemplatePageStory(contentful_id: { eq: $contentful_id }) {
      id
      title
      seoMetaDescription
      slug
      date
      imageHeaderDesktop {
          ...imageHeaderFragment
      }
      imageHeaderMobile {
          ...imageHeaderFragment
      }
      categories {
        id
        title
      }
      carrousel {
        title
        items {
          title
          blocText {
            content
          }
          smallImage {
              ...mediaFragment
          }
          largeImage {
              ...mediaFragment
          }
        }
      }
      carrouselPosition
      backgroundColor
      storyHtmlContent {
        content
      }
        secondaryBlocks {
            ... on ContentfulContentIframe {
                sys {
                    contentType {
                        sys {
                            id
                        }
                    }
                }
                id
                iframeUrl
            }
        ... on ContentfulComponentRedirectionHighlight {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          id
          ctaUrl
          ctaLabel
          description {
            content
          }
          name: title
        }
        ... on ContentfulComponentRichText {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          id
          title {
            id
            title
          }
          contentTextHtml {
            content
          }
          content {
            raw
          }
        }
        ... on ContentfulComponentSlider {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          id
          name: title
          slides {
            id
            image {
                ...mediaFragment
            }
            title
            text {
              content
            }
          }
        }
        ... on ContentfulContentBlocWithDocuments {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          id
          name: title
          blocDocuments {
              ...documentFragment
          }
        }
        ... on ContentfulContentMedia {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          ...mediaFragment
        }
      }
    }
  }
`
